import type { ReactNode } from 'react';

interface StatTileProps {
  icon: ReactNode;
  value: string;
  label: string;
  color?: string;
  onClick?: () => void;
}

export function StatTile({ icon, value, label, color, onClick }: StatTileProps) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      onClick={onClick}
      className={`flex flex-col items-start gap-1 rounded-[var(--radius-card)] bg-surface p-3 text-left shadow-[var(--shadow-card)] ${
        onClick ? 'transition-transform active:scale-[0.98]' : ''
      }`}
    >
      <span className="text-[18px] leading-none" aria-hidden>
        {icon}
      </span>
      <span className="tabular text-[22px] font-bold leading-tight" style={color ? { color } : undefined}>
        {value}
      </span>
      <span className="text-[12px] font-medium text-muted">{label}</span>
    </Tag>
  );
}

export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded-[var(--radius-btn)] bg-surface-2 ${className}`} />;
}

interface RatingProps {
  value: number | null;
  onChange: (value: number) => void;
  label: string;
  faces?: string[];
}

const DEFAULT_FACES = ['😞', '🙁', '😐', '🙂', '😄'];

/** 1-5 picker with faces. Used for mood, stress and craving intensity. */
export function Rating({ value, onChange, label, faces = DEFAULT_FACES }: RatingProps) {
  return (
    <fieldset>
      <legend className="mb-2 text-[13px] font-medium text-muted">{label}</legend>
      <div className="flex gap-2">
        {faces.map((face, index) => {
          const rating = index + 1;
          const selected = value === rating;
          return (
            <button
              key={rating}
              type="button"
              onClick={() => onChange(rating)}
              aria-pressed={selected}
              aria-label={`${label}: ${rating} de 5`}
              className={`flex h-12 flex-1 items-center justify-center rounded-[var(--radius-btn)] text-[22px] transition-all ${
                selected ? 'bg-brand/15 ring-2 ring-brand' : 'bg-surface-2'
              }`}
            >
              <span aria-hidden>{face}</span>
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
